import * as React from 'react'
import { useEffect, useState } from 'react'
import { Checkbox, Dialog, DialogContent, DialogTitle, makeStyles } from '@material-ui/core'
import { useTranslate } from 'react-admin'
import ButtonWithLoader from '../ui/ButtonWithLoader/ButtonWithLoader'
import getConsentStatus from '../../Apis/consent/getConsentStatus'
import getTos from '../../Apis/consent/getTos'
import acceptConsent from '../../Apis/consent/acceptConsent'
import consentPopupStyles from './consentPopup.styles'
import TERMS_AND_PRIVACY_URLS from '../../utils/constants/termsAndPrivacyUrls'

const ConsentPopup = () => {
  const translate = useTranslate()
  const useStyles = makeStyles(consentPopupStyles)
  const classes = useStyles()

  const [tos, setTos] = useState(null)
  const [tosAccepted, setTosAccepted] = useState(false)
  const [marketing, setMarketing] = useState(false)
  const [dataSharing, setDataSharing] = useState(false)
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false

    const decide = async () => {
      // ConsentPopup is mounted inside MyLayout, so reaching it implies the user
      // is authenticated. Still, getConsentStatus may 401 right after a logout —
      // catch quietly and leave the popup closed.
      let status
      try {
        status = await getConsentStatus()
      } catch {
        return
      }
      if (status?.hasAcceptedCurrentTos) return

      let currentTos
      try {
        currentTos = await getTos()
      } catch {
        return
      }

      if (cancelled) return
      setTos(currentTos)
      setTosAccepted(false)
      setMarketing(false)
      setDataSharing(false)
      setOpen(true)
    }

    decide()
    return () => {
      cancelled = true
    }
  }, [])

  const handleSave = async () => {
    if (!tosAccepted || saving || !tos) return
    setSaving(true)
    try {
      await acceptConsent({
        tosVersionId: tos.tosVersionId,
        marketingConsent: marketing,
        dataSharingConsent: dataSharing,
      })
      setOpen(false)
    } catch (err) {
      // axiosInstance interceptor already surfaces a snackbar for non-401 errors.
      console.error('Failed to save consent:', err)
    } finally {
      setSaving(false)
    }
  }

  if (!open || !tos) return null

  return (
    <Dialog open={open} disableScrollLock fullWidth maxWidth='sm' disableEscapeKeyDown>
      <DialogTitle classes={{ root: classes.dialogTitleRoot }}>
        <span className={classes.title}>{translate('ra.consentPopup.title')}</span>
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContentRoot }}>
        <div className={classes.subtitle}>{translate('ra.consentPopup.subtitle')}</div>

        <div className={classes.tosScroll}>{tos.body}</div>

        <div className={classes.sectionHeader}>{translate('ra.consentPopup.required')}</div>
        <div className={classes.checkboxRow}>
          <Checkbox
            checked={tosAccepted}
            onChange={(e) => setTosAccepted(e.target.checked)}
            color='primary'
            size='small'
            style={{ padding: 0, marginTop: 2 }}
          />
          <div className={classes.checkboxText}>
            <span className={classes.requiredStar}>* </span>
            {translate('ra.consentPopup.acceptTermsPrefix')}{' '}
            <a
              style={{ color: '#E0726E' }}
              href={TERMS_AND_PRIVACY_URLS.TERMS_OF_USE}
              target='_blank'
              rel='noopener noreferrer'
            >
              {translate('ra.consentPopup.termsOfUse')}
            </a>{' '}
            {translate('ra.consentPopup.and')}{' '}
            <a
              style={{ color: '#E0726E' }}
              href={TERMS_AND_PRIVACY_URLS.PRIVACY_POLICY}
              target='_blank'
              rel='noopener noreferrer'
            >
              {translate('ra.consentPopup.privacyPolicy')}
            </a>
            .
          </div>
        </div>

        <div className={classes.sectionHeader}>{translate('ra.consentPopup.optional')}</div>
        <div className={classes.checkboxRow}>
          <Checkbox
            checked={marketing}
            onChange={(e) => setMarketing(e.target.checked)}
            color='primary'
            size='small'
            style={{ padding: 0, marginTop: 2 }}
          />
          <div className={classes.checkboxText}>
            {translate('ra.consentPopup.marketingConsent')}
          </div>
        </div>
        <div className={classes.checkboxRow}>
          <Checkbox
            checked={dataSharing}
            onChange={(e) => setDataSharing(e.target.checked)}
            color='primary'
            size='small'
            style={{ padding: 0, marginTop: 2 }}
          />
          <div className={classes.checkboxText}>
            {translate('ra.consentPopup.dataSharingConsent')}
          </div>
        </div>

        <div className={classes.buttonWrapper}>
          <ButtonWithLoader
            type='button'
            label={translate('ra.consentPopup.save')}
            disabled={!tosAccepted}
            loading={saving}
            onClick={handleSave}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ConsentPopup
